/*
  7Raw Carnet Noir notebook loader (plan 2026-10-13 track C4). Small and deferred; the notebook itself
  (7r-notebook.js, 7r-notebook.css, La Belle Aurore and every notebook image) loads only when it is opened.

  Include it once on every page that offers the notebook (the Carnet Noir index, the home stage):

    <script
      src="{{ '7r-notebook-loader.js' | asset_url }}"
      defer="defer"
      data-r7-notebook-loader
      data-module="{{ '7r-notebook.js' | asset_url }}"
      data-css="{{ '7r-notebook.css' | asset_url }}"
      data-paper="{{ '7r-paper.png' | asset_url }}"
      data-page="{{ pages['carnet-noir'].url | default: '/pages/carnet-noir' }}"
    ></script>

  The data attributes are optional: without them the asset URLs are derived from this script's own URL (unversioned)
  and the page falls back to /pages/carnet-noir. Including it twice is harmless.

  Opening:
  - R7.openNotebook(opener) opens the notebook and returns a promise of the open instance (null on failure). It is
    idempotent: while the notebook is loading or open, further calls return the same promise / instance. Focus goes
    back to `opener` on close.
  - Clicks on any [data-r7-notebook-open] element are intercepted (plain left clicks only; a modified click still
    follows the link, e.g. into a new tab). A link should point at the index page, ideally with #notebook, so it
    works without JavaScript too.
  - A page that holds the notebook data opens it on load, and on hashchange, when the URL hash is #notebook.
  - If the notebook cannot open here (the data or the engine failed to load), the visitor is sent to the index page
    with #notebook added, and the index opens the book from the hash.
  - R7.closeNotebook() closes it.

  Data: the page's <script type="application/json" data-r7-notebook-data> (sections/7r-carnet-notebook.liquid).
  On any other page the index page itself is fetched (plain HTML, no ?sections= and no JSON Accept header), parsed
  with DOMParser (nothing in it runs or loads) and the same script is read from it. <page> is the opener's
  data-r7-notebook-page, else its href path, else data-page above. The Section Rendering API is not used on purpose:
  a section placed in a JSON template has a generated id (template--<id>__notebook), which another page cannot know,
  and ?sections=notebook answers {"notebook": null}.

  Events: document receives `r7:notebook` with detail { open: true | false } when it opens and closes.
*/
(() => {
  const script = document.currentScript || document.querySelector('script[data-r7-notebook-loader]');
  const ATTR_DATA = 'script[data-r7-notebook-data]';
  const FONT_URL = 'https://fonts.googleapis.com/css2?family=La+Belle+Aurore&display=swap';

  const sibling = (name) => {
    const src = script && script.src;
    if (!src) return '';
    const url = new URL(src, document.baseURI);
    url.pathname = url.pathname.replace(/7r-notebook-loader(\.min)?\.js$/, name);
    url.search = '';
    return url.href;
  };

  const ds = (script && script.dataset) || {};
  const config = {
    module: ds.module || sibling('7r-notebook.js'),
    css: ds.css || sibling('7r-notebook.css'),
    paper: ds.paper || sibling('7r-paper.png'),
    page: ds.page || '/pages/carnet-noir',
  };

  const cache = new Map();
  let current = null;
  let pending = null;
  let cssPromise = null;

  const absolute = (url) => new URL(url, document.baseURI).href;

  const loadFont = () => {
    if (document.querySelector('link[data-r7-notebook-font]')) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = FONT_URL;
    link.dataset.r7NotebookFont = '';
    document.head.append(link);
  };

  const loadCss = () => {
    if (cssPromise) return cssPromise;
    cssPromise = new Promise((resolve, reject) => {
      if (document.querySelector('link[data-r7-notebook-css]')) {
        resolve();
        return;
      }
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = config.css;
      link.dataset.r7NotebookCss = '';
      link.onload = () => resolve();
      link.onerror = () => {
        link.remove();
        cssPromise = null;
        reject(new Error('7r-notebook.css did not load'));
      };
      document.head.append(link);
    });
    return cssPromise;
  };

  const pagePath = (opener) => {
    const explicit = opener && opener.getAttribute && opener.getAttribute('data-r7-notebook-page');
    const href = opener && opener.getAttribute && opener.getAttribute('href');
    const raw = explicit || (href && !href.startsWith('#') ? href : '') || config.page;
    const url = new URL(raw, document.baseURI);
    url.hash = '';
    url.search = '';
    return url;
  };

  const parseData = (scriptEl) => {
    if (!scriptEl) throw new Error('No notebook data found');
    return JSON.parse(scriptEl.textContent);
  };

  const readData = async (opener) => {
    const local = document.querySelector(ATTR_DATA);
    if (local) return parseData(local);

    const url = pagePath(opener);
    const key = url.pathname;
    if (cache.has(key)) return cache.get(key);

    // The whole index page, as a visitor would get it. Not ?sections=: a JSON-template section's id is generated.
    const response = await fetch(url.href, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Notebook page request failed (${response.status})`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const data = parseData(doc.querySelector(ATTR_DATA));
    cache.set(key, data);
    return data;
  };

  const setBusy = (opener, busy) => {
    if (!opener || !opener.setAttribute) return;
    if (busy) opener.setAttribute('aria-busy', 'true');
    else opener.removeAttribute('aria-busy');
  };

  // Go to the index page with #notebook, where the book opens from the hash. Never reload the page we are on.
  const fallback = (opener) => {
    const url = pagePath(opener);
    if (url.origin !== location.origin || url.pathname === location.pathname) return;
    url.hash = 'notebook';
    location.assign(url.href);
  };

  const clearHash = () => {
    if (location.hash === '#notebook') history.replaceState(history.state, '', location.pathname + location.search);
  };

  const openNotebook = (opener) => {
    if (current) return Promise.resolve(current);
    if (pending) return pending;
    const el = opener instanceof Element ? opener : null;
    setBusy(el, true);
    pending = (async () => {
      const data = await readData(el);
      loadFont();
      const [mod] = await Promise.all([import(absolute(config.module)), loadCss()]);
      if (current) return current;
      current = mod.open(data, {
        opener: el,
        paper: config.paper ? absolute(config.paper) : '',
        onClose: () => {
          current = null;
          clearHash();
          document.dispatchEvent(new CustomEvent('r7:notebook', { detail: { open: false } }));
        },
      });
      document.dispatchEvent(new CustomEvent('r7:notebook', { detail: { open: true } }));
      return current;
    })()
      .catch((error) => {
        console.error('[7r-notebook]', error);
        fallback(el);
        return null;
      })
      .finally(() => {
        pending = null;
        setBusy(el, false);
      });
    return pending;
  };

  const closeNotebook = (options) => {
    if (current) current.close(options);
  };

  const fromHash = () => {
    if (location.hash !== '#notebook' || !document.querySelector(ATTR_DATA)) return;
    openNotebook(document.querySelector('[data-r7-notebook-open]'));
  };

  const install = () => {
    const R7 = window.R7;
    if (!R7) return false;
    if (R7.openNotebook && R7.openNotebook.r7Loader) return true;
    openNotebook.r7Loader = true;
    R7.openNotebook = openNotebook;
    R7.closeNotebook = closeNotebook;

    document.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const opener = event.target instanceof Element ? event.target.closest('[data-r7-notebook-open]') : null;
      if (!opener) return;
      event.preventDefault();
      openNotebook(opener);
    });

    window.addEventListener('hashchange', fromHash);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fromHash, { once: true });
    else fromHash();

    // Theme editor: selecting the notebook section opens the book, deselecting closes it, and a settings change
    // reopens it with the new data.
    const isNotebookSection = (event) => event.target instanceof Element && !!event.target.querySelector(ATTR_DATA);
    document.addEventListener('shopify:section:select', (event) => {
      if (isNotebookSection(event)) openNotebook(null);
    });
    document.addEventListener('shopify:section:deselect', (event) => {
      if (isNotebookSection(event)) closeNotebook();
    });
    document.addEventListener('shopify:section:load', (event) => {
      if (!isNotebookSection(event) || !current) return;
      closeNotebook({ instant: true });
      openNotebook(null);
    });
    return true;
  };

  // 7r-global.js (deferred in <head>) creates window.R7 before this deferred script runs; never create it here,
  // or 7r-global.js would skip its own setup.
  if (!install()) document.addEventListener('DOMContentLoaded', install, { once: true });
})();
